/**
 * 统一状态字典（2026-10-07）：原始状态值 → 中文标签 + antd Tag 颜色。
 *
 * 用法：<StatusTag status={record.status} />（shared/components/StatusTag.jsx），
 * 或 statusLabel(status) 取文字。按 domain 区分同名状态在不同产品里的含义（如 active：
 * 订阅=生效中，实例=运行中）。未收录的值原样显示，不会显示 undefined。
 */

const COMMON = {
    active: ['正常', 'success'],
    enabled: ['已启用', 'success'],
    disabled: ['已禁用', 'default'],
    inactive: ['未启用', 'default'],
    pending: ['处理中', 'processing'],
    processing: ['处理中', 'processing'],
    creating: ['创建中', 'processing'],
    provisioning: ['开通中', 'processing'],
    deploying: ['部署中', 'processing'],
    running: ['运行中', 'success'],
    starting: ['启动中', 'processing'],
    stopping: ['停止中', 'warning'],
    stopped: ['已停止', 'default'],
    exited: ['已停止', 'default'],
    suspended: ['已暂停', 'warning'],
    error: ['异常', 'error'],
    failed: ['失败', 'error'],
    deleted: ['已删除', 'default'],
    deleting: ['删除中', 'processing'],
    cancelled: ['已取消', 'default'],
    canceled: ['已取消', 'default'],
    expired: ['已过期', 'error'],
    terminated: ['已终止', 'default'],
    unknown: ['未知', 'default'],
    // 证书
    issued: ['已签发', 'success'],
    valid: ['有效', 'success'],
    pending_validation: ['待验证', 'processing'],
    pending_issuance: ['签发中', 'processing'],
    awaiting_configuration: ['待配置', 'warning'],
    revoked: ['已吊销', 'default'],
    rejected: ['已拒绝', 'error'],
    // 域名
    pending_delete: ['待删除', 'error'],
    pending_transfer: ['转入中', 'processing'],
    redemption: ['赎回期', 'error'],
    grace: ['宽限期', 'warning'],
    locked: ['已锁定', 'default'],
    transferred_out: ['已转出', 'default'],
    // 账单 / 订阅
    paid: ['已支付', 'success'],
    unpaid: ['未支付', 'warning'],
    overdue: ['已逾期', 'error'],
    refunded: ['已退款', 'default'],
    trial: ['试用中', 'processing'],
    // 计费周期 / 类型（偶尔被当作状态渲染）
    onetime: ['一次性', 'default'],
    monthly: ['月付', 'default'],
    quarterly: ['季付', 'default'],
    semi_annually: ['半年付', 'default'],
    annually: ['年付', 'default'],
};

const DOMAINS = {
    vm: { active: ['运行中', 'success'] },
    service: { active: ['运行中', 'success'] },
    subscription: { active: ['生效中', 'success'], pending: ['待支付', 'warning'] },
    zone: { active: ['正常', 'success'], pending: ['待接入', 'warning'] },
    cdn: { active: ['已启用', 'success'], pending: ['待接入', 'warning'] },
    cert: { active: ['有效', 'success'], pending: ['待处理', 'processing'] },
    eip: { available: ['未绑定', 'orange'], bound: ['已绑定', 'success'] },
    // 云服务器生命周期状态（VMs.status）+ libvirt 电源状态（/state）
    evm: {
        create: ['创建中', 'processing'],
        rebuild: ['重装中', 'processing'],
        active: ['正常', 'success'],
        suspend: ['已暂停', 'warning'],
        flow_limit: ['流量超限', 'gold'],
        migrate: ['迁移中', 'processing'],
        delete: ['删除中', 'default'],
        error: ['异常', 'error'],
        running: ['运行中', 'success'],
        shutdown: ['已关机', 'default'],
        shutoff: ['已关机', 'default'],
        paused: ['已暂停', 'warning'],
    },
};

const norm = (s) => String(s ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');

export const resolveStatus = (status, domain) => {
    const key = norm(status);
    if (!key) return { label: '-', color: 'default' };
    const hit = (domain && DOMAINS[domain] && DOMAINS[domain][key]) || COMMON[key];
    return hit ? { label: hit[0], color: hit[1] } : { label: String(status), color: 'default' };
};

export const statusLabel = (status, domain) => resolveStatus(status, domain).label;
export const statusColor = (status, domain) => resolveStatus(status, domain).color;

export default COMMON;
